/**
 * ExpensesPage - Refactored Version
 * Uses shared components and hooks for cleaner, maintainable code.
 */
import { useEffect, useState, useMemo } from 'react';
import { expenseAPI, exportAPI } from '../lib/api';
import ExportButton from '../components/ExportButton';
import { useAuth } from '../context/AuthContext';
import { useApi, useMutation } from '../hooks/useApi';
import { useTable } from '../hooks/useTable';
import { formatDate, formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Check, X, Loader2, Banknote, Clock, CheckCircle, XCircle, Receipt, Users, FileText, ChevronRight } from 'lucide-react';

// Shared components
import { DataTable } from '../components/shared/DataTable';
import { SearchInput } from '../components/shared/SearchInput';
import { PageSkeleton } from '../components/shared/LoadingSkeleton';

// Constants
const CATEGORIES = ['Travel', 'Meals', 'Supplies', 'Equipment', 'Software', 'Training', 'Other'];

const STATUS_CONFIG = {
  pending: { label: 'Pending Manager', variant: 'secondary', icon: Clock },
  manager_approved: { label: 'Pending Final', variant: 'outline', icon: Clock },
  hr_approved: { label: 'HR Approved', variant: 'outline', icon: CheckCircle },
  approved: { label: 'Approved', variant: 'default', icon: CheckCircle },
  rejected: { label: 'Rejected', variant: 'destructive', icon: XCircle },
  paid: { label: 'Paid', variant: 'default', icon: Banknote },
};

const defaultForm = { amount: '', category: 'Travel', description: '', receipt_url: '' };

export default function ExpensesPage() {
  const { isEmployee, isAdmin, isSuperAdmin, isAccounts, canApproveExpense } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({ open: false, expense: null, level: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [formData, setFormData] = useState(defaultForm);

  // Data fetching
  const { data: expenses, loading, refresh } = useApi(expenseAPI.getAll);
  const { data: teamExpenses, execute: fetchTeam } = useApi(expenseAPI.getMyTeam, { immediate: false });

  // Create mutation
  const { mutate: createExpense, loading: creating } = useMutation(expenseAPI.create, {
    successMessage: 'Expense claim submitted',
    onSuccess: () => { setDialogOpen(false); setFormData(defaultForm); refresh(); }
  });

  // Fetch team expenses for managers
  useEffect(() => { if (!isEmployee) fetchTeam(); }, [isEmployee]);

  // Table hook
  const { items: filteredExpenses, search, setSearch, sort, setSort } = useTable(expenses || [], {
    searchFields: ['employee_name', 'category', 'description'],
    defaultSort: { field: 'created_at', order: 'desc' }
  });

  // Stats
  const stats = useMemo(() => ({
    pending: (expenses || []).filter(e => e.status === 'pending').length,
    managerApproved: (expenses || []).filter(e => e.status === 'manager_approved').length,
    approved: (expenses || []).filter(e => e.status === 'approved').length,
    total: (expenses || []).reduce((sum, e) => sum + (e.amount || 0), 0),
  }), [expenses]);

  const pendingTeam = useMemo(() => (teamExpenses || []).filter(e => e.status === 'pending'), [teamExpenses]);
  const pendingFinal = useMemo(() => (expenses || []).filter(e => e.status === 'manager_approved'), [expenses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await createExpense({ ...formData, amount: parseFloat(formData.amount) });
  };

  const handleApproval = async (expense, action, level) => {
    if (action === 'reject') {
      setRejectDialog({ open: true, expense, level });
      return;
    }
    try {
      if (level === 'manager') await expenseAPI.managerApprove(expense.id, 'approve');
      else await expenseAPI.finalApprove(expense.id, 'approve');
      toast.success(`Expense ${action}d`);
      refresh(); if (!isEmployee) fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const handleReject = async () => {
    const { expense, level } = rejectDialog;
    if (!expense) return;
    try {
      if (level === 'manager') await expenseAPI.managerApprove(expense.id, 'reject', rejectReason);
      else await expenseAPI.finalApprove(expense.id, 'reject', rejectReason);
      toast.success('Expense rejected');
      setRejectDialog({ open: false, expense: null, level: '' });
      setRejectReason('');
      refresh(); if (!isEmployee) fetchTeam();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      await expenseAPI.markPaid(id);
      toast.success('Marked as paid');
      refresh();
    } catch (error) {
      toast.error('Failed to mark as paid');
    }
  };

  // Table columns
  const baseColumns = [
    { key: 'employee_name', label: 'Employee', sortable: true, render: (row) => <span className="font-medium">{row.employee_name || 'N/A'}</span> },
    { key: 'amount', label: 'Amount', sortable: true, render: (row) => <span className="font-semibold">{formatCurrency(row.amount)}</span> },
    { key: 'category', label: 'Category', render: (row) => <Badge variant="outline">{row.category}</Badge> },
    { key: 'description', label: 'Description', render: (row) => <span className="max-w-xs truncate block">{row.description}</span> },
    { key: 'created_at', label: 'Date', sortable: true, render: (row) => formatDate(row.created_at) },
    { key: 'status', label: 'Status', render: (row) => {
      const config = STATUS_CONFIG[row.status] || STATUS_CONFIG.pending;
      const Icon = config.icon;
      return <Badge variant={config.variant} className="flex items-center gap-1 w-fit"><Icon className="w-3 h-3" />{config.label}</Badge>;
    }},
  ];

  const actionColumn = (level) => ({
    key: 'actions', label: '', render: (row) => {
      const pendingStatus = level === 'manager' ? 'pending' : 'manager_approved';
      if (row.status !== pendingStatus) return null;
      return (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" className="text-green-600" onClick={(e) => { e.stopPropagation(); handleApproval(row, 'approve', level); }}>
            <Check className="w-4 h-4 mr-1" /> Approve
          </Button>
          <Button size="sm" variant="outline" className="text-red-600" onClick={(e) => { e.stopPropagation(); handleApproval(row, 'reject', level); }}>
            <X className="w-4 h-4 mr-1" /> Reject
          </Button>
          {isAccounts && row.status === 'approved' && (
            <Button size="sm" variant="default" onClick={(e) => { e.stopPropagation(); handleMarkPaid(row.id); }}>
              <Banknote className="w-4 h-4 mr-1" /> Mark Paid
            </Button>
          )}
        </div>
      );
    }
  });

  if (loading) return <PageSkeleton />;

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Expense Management</h1>
          <p className="text-muted-foreground mt-1">Submit and track expense reimbursements</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton 
            exportFn={exportAPI.expenses} 
            filename="expenses.csv" 
            label="Export"
          />
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full"><Plus className="w-4 h-4 mr-2" /> New Expense</Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Submit Expense Claim</DialogTitle>
              <DialogDescription>Enter expense details for reimbursement</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount (₹)</Label>
                  <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Receipt URL (optional)</Label>
                <Input value={formData.receipt_url} onChange={(e) => setFormData({ ...formData, receipt_url: e.target.value })} placeholder="https://..." />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={creating}>{creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      {(isAdmin || isSuperAdmin || isAccounts) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all" onClick={() => setSearch('pending')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div><div className="text-muted-foreground text-sm mb-1">Pending Manager</div><p className="text-2xl font-bold">{stats.pending}</p></div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all" onClick={() => setSearch('manager_approved')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div><div className="text-muted-foreground text-sm mb-1">Pending Final</div><p className="text-2xl font-bold">{stats.managerApproved}</p></div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all" onClick={() => setSearch('approved')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div><div className="text-green-600 text-sm mb-1">Approved</div><p className="text-2xl font-bold">{stats.approved}</p></div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-muted-foreground text-sm mb-1">Total Amount</div>
              <p className="text-2xl font-bold">{formatCurrency(stats.total)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <SearchInput value={search} onChange={setSearch} placeholder="Search expenses..." className="max-w-md" />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all"><FileText className="w-4 h-4 mr-2" />All Expenses</TabsTrigger>
          {!isEmployee && <TabsTrigger value="team"><Users className="w-4 h-4 mr-2" />My Team{pendingTeam.length > 0 && <Badge variant="secondary" className="ml-1">{pendingTeam.length}</Badge>}</TabsTrigger>}
          {canApproveExpense && <TabsTrigger value="final"><CheckCircle className="w-4 h-4 mr-2" />Final Approval{pendingFinal.length > 0 && <Badge variant="secondary" className="ml-1">{pendingFinal.length}</Badge>}</TabsTrigger>}
        </TabsList>

        <TabsContent value="all">
          <Card><CardContent className="p-0"><DataTable data={filteredExpenses} columns={baseColumns} sort={sort} onSort={setSort} emptyMessage="No expenses found" /></CardContent></Card>
        </TabsContent>

        {!isEmployee && (
          <TabsContent value="team">
            <Card>
              <CardHeader><CardTitle className="text-lg">Team Expenses (Manager Approval)</CardTitle></CardHeader>
              <CardContent className="p-0"><DataTable data={pendingTeam} columns={[...baseColumns, actionColumn('manager')]} emptyMessage="No pending team expenses" /></CardContent>
            </Card>
          </TabsContent>
        )}

        {canApproveExpense && (
          <TabsContent value="final">
            <Card>
              <CardHeader><CardTitle className="text-lg">Pending Final Approval</CardTitle></CardHeader>
              <CardContent className="p-0"><DataTable data={pendingFinal} columns={[...baseColumns, actionColumn('final')]} emptyMessage="No expenses pending final approval" /></CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => !open && setRejectDialog({ open: false, expense: null, level: '' })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Expense</DialogTitle><DialogDescription>Provide a reason</DialogDescription></DialogHeader>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason..." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, expense: null, level: '' })}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} disabled={!rejectReason.trim()}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
